import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, fileUrl } from "@/lib/api";
import { MessageCircle, Globe, Instagram, MapPin, ArrowLeft, Pencil, Trash2, User2, Eye, TrendingUp } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import VerifiedBadge from "@/components/VerifiedBadge";

export default function BusinessDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [b, setB] = useState(null);

  useEffect(() => { (async () => {
    try {
      const { data } = await api.get(`/businesses/${id}`);
      setB(data);
      // Track view (server ignores owner's own views)
      api.post(`/businesses/${id}/track/view`).catch((err) => console.warn("Failed to track view:", err));
    } catch (err) {
      console.error("Failed to load business:", err);
      toast.error("Gagal memuat bisnis");
    }
  })(); }, [id]);

  const trackAndOpenWA = (phone) => {
    api.post(`/businesses/${id}/track/wa`).catch((err) => console.warn("Failed to track WA click:", err));
    window.open(`https://wa.me/${phone.replace(/\D/g, "")}`, "_blank", "noopener,noreferrer");
  };

  const onDelete = async () => {
    if (!window.confirm("Hapus bisnis ini?")) return;
    try { await api.delete(`/businesses/${id}`); toast.success("Bisnis dihapus"); nav("/app"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  if (!b) return <Layout><div className="text-bizma-subtext">Memuat...</div></Layout>;
  const isOwner = user?.user_id === b.owner_id;
  const canEdit = isOwner || user?.role === "admin";

  return (
    <Layout>
      <Link to="/app" className="inline-flex items-center gap-2 text-sm text-bizma-subtext mb-6" data-testid="back-to-directory">
        <ArrowLeft size={16}/> Kembali
      </Link>
      <div className="grid lg:grid-cols-3 gap-6" data-testid="business-detail">
        <div className="lg:col-span-2 felt-card overflow-hidden">
          <div className="aspect-video bg-bizma-muted">
            {b.photo_path && <img src={fileUrl(b.photo_path)} alt={b.name} className="w-full h-full object-cover" />}
          </div>
          <div className="p-6 sm:p-8">
            <div className="text-xs uppercase tracking-widest text-bizma-sage font-bold">{b.category}</div>
            <h1 className="text-3xl font-display font-bold mt-2">{b.name}</h1>
            <p className="mt-4 text-[#4A4743] leading-relaxed whitespace-pre-line">{b.description}</p>
            {b.gallery && b.gallery.length > 0 && (
              <div className="mt-6" data-testid="business-gallery">
                <div className="text-xs uppercase tracking-widest text-bizma-sage font-bold mb-3">Galeri</div>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {b.gallery.map((p) => (
                    <a key={p} href={fileUrl(p)} target="_blank" rel="noreferrer" className="aspect-square rounded-2xl overflow-hidden border border-bizma-border block hover:opacity-90 transition-opacity">
                      <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                    </a>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-6 grid sm:grid-cols-2 gap-3 text-sm">
              {b.address && <InfoRow icon={<MapPin size={16}/>} label="Alamat" value={b.address} />}
              {b.website && <InfoRow icon={<Globe size={16}/>} label="Website" value={<a href={b.website} target="_blank" rel="noreferrer" className="text-bizma-terracotta break-all">{b.website}</a>} />}
              {b.instagram && <InfoRow icon={<Instagram size={16}/>} label="Instagram" value={<a href={`https://instagram.com/${b.instagram.replace("@","")}`} target="_blank" rel="noreferrer" className="text-bizma-terracotta">{b.instagram}</a>} />}
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              {b.whatsapp && (
                <button onClick={() => trackAndOpenWA(b.whatsapp)}
                   className="btn-wa" data-testid="business-whatsapp-btn">
                  <MessageCircle size={18}/> Chat WhatsApp Bisnis
                </button>
              )}
              {canEdit && (
                <>
                  <Link to={`/app/businesses/${b.business_id}/edit`} className="btn-outline" data-testid="edit-business-btn">
                    <Pencil size={16}/> Edit
                  </Link>
                  <button onClick={onDelete} className="btn-outline text-red-600 border-red-200 hover:bg-red-50" data-testid="delete-business-btn">
                    <Trash2 size={16}/> Hapus
                  </button>
                </>
              )}
            </div>
            <div className="mt-6 flex items-center gap-6 text-sm text-bizma-subtext" data-testid="business-stats">
              <span className="inline-flex items-center gap-1"><Eye size={14}/> {b.views || 0} views</span>
              <span className="inline-flex items-center gap-1"><TrendingUp size={14}/> {b.wa_clicks || 0} klik WhatsApp</span>
            </div>
          </div>
        </div>

        <div className="felt-card p-6" data-testid="business-owner-card">
          <div className="text-xs uppercase tracking-widest text-bizma-sage font-bold">Pemilik</div>
          <Link to={`/app/members/${b.owner?.user_id}`} className="mt-4 flex items-center gap-4 group" data-testid="business-owner-link">
            {b.owner?.photo_path ? (
              <img src={fileUrl(b.owner.photo_path)} alt="" className="w-16 h-16 rounded-full object-cover ring-2 ring-bizma-terracotta/50" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-bizma-terracotta text-white grid place-items-center text-2xl font-semibold">{b.owner?.name?.charAt(0)}</div>
            )}
            <div>
              <div className="font-display font-semibold group-hover:text-bizma-terracotta transition-colors flex items-center gap-1">
                {b.owner?.name}
                {b.owner?.is_verified && <VerifiedBadge size={16} />}
              </div>
              <div className="text-sm text-bizma-subtext">{b.owner?.city || "-"}</div>
              <div className="mt-1 inline-flex items-center gap-1 text-xs text-bizma-sage"><User2 size={12}/> Lihat Profil</div>
            </div>
          </Link>
          {b.owner?.whatsapp && (
            <button onClick={() => { api.post(`/businesses/${id}/track/wa`).catch((err) => console.warn("Failed to track WA click:", err)); window.open(`https://wa.me/${b.owner.whatsapp.replace(/\D/g, "")}`, "_blank", "noopener,noreferrer"); }}
               className="mt-4 btn-wa w-full text-sm py-2" data-testid="owner-whatsapp-btn">
              <MessageCircle size={16}/> Chat Pemilik
            </button>
          )}
        </div>
      </div>
    </Layout>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-bizma-sage mt-0.5">{icon}</span>
      <div><div className="text-xs text-bizma-subtext">{label}</div><div className="text-bizma-ink">{value}</div></div>
    </div>
  );
}
